import { Button, Textarea } from "@medusajs/ui"
import { useEffect, useRef, useState } from "react"
import { safeHtml } from "@/lib/sanitize"

/**
 * Shopify-style rich text editor: bold, italic, underline, headings, lists,
 * links, and a "Show HTML" toggle. Dependency-free (contentEditable). The
 * HTML is sanitised when loaded into the editor and again wherever it's shown
 * (admin: DOMPurify, storefront: sanitize-html), so pasted markup can't run.
 */
export function RichTextEditor({ value, onChange, label, minHeight = 180 }: { value: string; onChange: (html: string) => void; label?: string; minHeight?: number }) {
  const ref = useRef<HTMLDivElement>(null)
  const [html, setHtml] = useState(false)
  const last = useRef(value)

  // Load external value changes (discard, async load) without clobbering typing.
  useEffect(() => {
    if (ref.current && value !== last.current) {
      ref.current.innerHTML = safeHtml(value)
      last.current = value
    }
  }, [value])
  useEffect(() => {
    if (ref.current) ref.current.innerHTML = safeHtml(value)
  }, [html]) // eslint-disable-line react-hooks/exhaustive-deps

  const emit = () => {
    const out = ref.current?.innerHTML ?? ""
    const clean = out === "<br>" ? "" : out
    last.current = clean
    onChange(clean)
  }
  const cmd = (name: string, arg?: string) => {
    ref.current?.focus()
    document.execCommand(name, false, arg)
    emit()
  }
  const link = () => {
    const url = window.prompt("Link URL (https://… or /pages/…)")
    if (!url) return
    if (!/^(https:\/\/|\/(?!\/)|mailto:)/i.test(url.trim())) return window.alert("Use an https:// link, a /path on your store, or mailto:")
    cmd("createLink", url.trim())
  }

  const B = ({ onClick, title, children }: { onClick: () => void; title: string; children: React.ReactNode }) => (
    <button type="button" title={title} aria-label={title} onMouseDown={(e) => e.preventDefault()} onClick={onClick} className="rounded px-2 py-1 text-[13px] text-ui-fg-subtle hover:bg-ui-bg-base-hover disabled:opacity-40" disabled={html}>
      {children}
    </button>
  )

  return (
    <div className="overflow-hidden rounded-lg border border-ui-border-base focus-within:border-ui-border-interactive">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-ui-border-base bg-ui-bg-subtle px-1 py-1">
        <select data-bare
          aria-label="Text style"
          disabled={html}
          className="h-7 rounded bg-transparent px-1 text-[13px] text-ui-fg-subtle outline-none"
          onMouseDown={(e) => e.stopPropagation()}
          onChange={(e) => {
            cmd("formatBlock", e.target.value)
            e.target.value = ""
          }}
          defaultValue=""
        >
          <option value="" disabled>Paragraph</option>
          <option value="P">Paragraph</option>
          <option value="H2">Heading 2</option>
          <option value="H3">Heading 3</option>
          <option value="H4">Heading 4</option>
          <option value="BLOCKQUOTE">Quote</option>
        </select>
        <B title="Bold" onClick={() => cmd("bold")}><b>B</b></B>
        <B title="Italic" onClick={() => cmd("italic")}><i>I</i></B>
        <B title="Underline" onClick={() => cmd("underline")}><u>U</u></B>
        <B title="Bulleted list" onClick={() => cmd("insertUnorderedList")}>• List</B>
        <B title="Numbered list" onClick={() => cmd("insertOrderedList")}>1. List</B>
        <B title="Insert link" onClick={link}>Link</B>
        <B title="Remove formatting" onClick={() => cmd("removeFormat")}>Clear</B>
        <span className="flex-1" />
        <Button type="button" size="small" variant="transparent" onClick={() => setHtml((h) => !h)}>
          {html ? "Hide HTML" : "Show HTML"}
        </Button>
      </div>
      {html ? (
        <Textarea aria-label={label ? `${label} HTML` : "HTML"} className="rounded-none border-0 font-mono text-[12px] shadow-none" rows={10} value={value} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <div
          ref={ref}
          role="textbox"
          aria-multiline="true"
          aria-label={label ?? "Rich text"}
          contentEditable
          suppressContentEditableWarning
          onInput={emit}
          onBlur={emit}
          onPaste={(e) => {
            // Paste as sanitised HTML (or text), never raw markup from other sites.
            e.preventDefault()
            const rich = e.clipboardData.getData("text/html")
            const text = e.clipboardData.getData("text/plain")
            document.execCommand("insertHTML", false, rich ? safeHtml(rich) : text.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" })[c]!).replace(/\n/g, "<br>"))
            emit()
          }}
          className="tf-rte bg-white px-3 py-2 text-[13px] outline-none"
          style={{ minHeight }}
        />
      )}
    </div>
  )
}
